<script setup lang="ts">
    import { computed } from 'vue'

    // ---------------- props / emits ----------------

    const props = defineProps<{
        propOpened: boolean
        propSurveyStarted: boolean
        propAutoRevealEnabled: boolean
        propNamesRevealed: boolean
    }>()

    const emit = defineEmits<{
        (e: 'start-meeting'): void
        (e: 'start-survey'): void
        (e: 'stop-survey'): void
        (e: 'toggle-auto-reveal'): void
        (e: 'toggle-names-revealed'): void
        (e: 'show-access-links'): void
        (e: 'close-dialog'): void
    }>()

    // ---------------- derived ----------------

    const opened = computed(() => props.propOpened)
    const surveyStarted = computed(() => props.propSurveyStarted)
    const autoRevealEnabled = computed(() => props.propAutoRevealEnabled)
    const namesRevealed = computed(() => props.propNamesRevealed)

    // ---------------- actions ----------------

    function startMeeting()
    {
        emit('start-meeting')
    }

    function startSurvey()
    {
        emit('start-survey')
    }

    function stopSurvey()
    {
        emit('stop-survey')
    }

    function toggleAutoReveal()
    {
        emit('toggle-auto-reveal')
    }

    function toggleNamesRevealed()
    {
        emit('toggle-names-revealed')
    }

    function showAccessLinks()
    {
        emit('show-access-links')
    }

    function close()
    {
        emit('close-dialog')
    }
</script>


<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-lg modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Set up your meeting​</h5>
                        <button type="button" class="close" @click="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <p class="lead">
                            Before you start your real-time meeting, let’s make sure everything is ready.
                            <strong>You can change any of these settings during the meeting.</strong>
                        </p><br />

                        <table class="table">
                            <tbody>
                                <tr v-if="surveyStarted === true">
                                    <td>
                                        <h5>Survey is open</h5>
                                        <p>Your team can change their answers to <strong>ANY question</strong> during the meeting. </p>
                                    </td>
                                    <td class="align-middle">
                                        <button class="btn btn-outline-success btn-block text-nowrap" @click="stopSurvey">Close survey</button>
                                    </td>
                                </tr>
                                <tr v-if="surveyStarted === false">
                                    <td>
                                        <h5>Survey is closed</h5>
                                        <p>Your team will only be able to change their answers for the <strong>question you’re on.</strong></p>
                                    </td>
                                    <td class="align-middle">
                                        <button class="btn btn-outline-secondary btn-block text-nowrap" @click="startSurvey">Open survey</button>
                                    </td>
                                </tr>
                                <tr v-if="autoRevealEnabled === true">
                                    <td>
                                        <h5>Auto reveal is ON</h5>
                                        <p>When you move from question to question, your meeting participants WILL be able to see each others’ votes.​</p>
                                    </td>
                                    <td class="align-middle">
                                        <button class="btn btn-outline-secondary btn-block text-nowrap" @click="toggleAutoReveal">Turn off</button>
                                    </td>
                                </tr>
                                <tr v-if="autoRevealEnabled === false">
                                    <td>
                                        <h5>Auto reveal is OFF</h5>
                                        <p>When you move to the next question, we will automatically hide everyone’s votes. This can help reduce “follow my leader” bias and “group think.”</p>
                                    </td>
                                    <td class="align-middle">
                                        <button class="btn btn-outline-secondary btn-block text-nowrap" @click="toggleAutoReveal">Turn on</button>
                                    </td>
                                </tr>
                                <tr v-if="namesRevealed === true">
                                    <td>
                                        <h5>Anonymous mode is OFF</h5>
                                        <p>When sharing votes, names WILL be displayed.​</p>
                                    </td>
                                    <td class="align-middle">
                                        <button class="btn btn-outline-secondary btn-block text-nowrap" @click="toggleNamesRevealed">Turn on</button>
                                    </td>
                                </tr>
                                <tr v-if="namesRevealed === false">
                                    <td>
                                        <h5>Anonymous mode is ON</h5>
                                        <p>When sharing votes, names WILL NOT be displayed.​</p>
                                    </td>
                                    <td class="align-middle">
                                        <button class="btn btn-outline-secondary btn-block text-nowrap" @click="toggleNamesRevealed">Turn off</button>
                                    </td>
                                </tr>
                                <tr>
                                    <td>
                                        <h5>Access links</h5>
                                        <p>These links will let your participants join the meeting with just one click. Why not (re)send them now?​</p>
                                    </td>
                                    <td class="align-middle">
                                        <button class="btn btn-outline-secondary btn-block text-nowrap" @click="showAccessLinks">Access links</button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div class="modal-footer">
                        <button @click="startMeeting" type="button" class="btn btn-primary">Start the meeting</button>
                        <button @click="close" type="button" class="btn btn-outline-secondary">Cancel</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>